import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Tag, Copy, Trash2, Users } from "lucide-react";

export default function PriceSheets() {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const load = () => api.get("/store/price-sheets").then((r) => setSheets(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Enter a name"); return; }
    const { data } = await api.post("/store/price-sheets", { name: name.trim() });
    setOpen(false); setName("");
    navigate(`/dashboard/price-sheets/${data.id}`);
  };

  const duplicate = async (id) => { await api.post(`/store/price-sheets/${id}/duplicate`); toast.success("Duplicated"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this price sheet? Customers will revert to base price.")) return; await api.delete(`/store/price-sheets/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Price Sheets</h1>
          <p className="text-muted-foreground mt-1">Reusable price lists like Retail, Wholesale, or VIP.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-lg" data-testid="add-price-sheet-btn"><Plus className="h-4 w-4 mr-1.5" /> New price sheet</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New price sheet</DialogTitle></DialogHeader>
            <div className="py-2 space-y-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Wholesale" data-testid="price-sheet-name-input" onKeyDown={(e) => e.key === "Enter" && create()} />
            </div>
            <DialogFooter><Button onClick={create} data-testid="create-price-sheet-btn">Create & set prices</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {sheets.length === 0 ? (
        <Card className="rounded-xl border-border p-12 text-center">
          <Tag className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="font-medium">No price sheets yet</p>
          <p className="text-sm text-muted-foreground mt-1">Create one to give groups of customers their own pricing.</p>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {sheets.map((s) => (
            <Card key={s.id} className="rounded-xl border-border p-5 group hover:shadow-md hover:-translate-y-1 transition-all duration-200" data-testid={`price-sheet-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center"><Tag className="h-5 w-5 text-primary" /></div>
                <div className="flex gap-1">
                  <button onClick={() => duplicate(s.id)} className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-secondary" title="Duplicate" data-testid={`duplicate-${s.id}`}><Copy className="h-4 w-4" /></button>
                  <button onClick={() => del(s.id)} className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-secondary" title="Delete" data-testid={`delete-sheet-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <button onClick={() => navigate(`/dashboard/price-sheets/${s.id}`)} className="text-left w-full mt-3">
                <h3 className="font-semibold hover:text-primary transition-colors">{s.name}</h3>
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-3">
                  <span>{s.product_count} priced</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {s.customer_count}</span>
                </p>
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
